/**
 * Schemas Zod e tipos da API de corpo, progresso, plano, histórico e perfil (7.2, 7.3, 7.4, 7.9).
 */
import { z } from 'zod';
import type { Constancy, DayState } from '../rules/history';
import type { Forecast, ProgressAlertDraft, WeightChart } from '../rules/weight';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Horário no formato HH:MM');

export const sleepBucketSchema = z.enum(['lt6', 'h6to7', 'h7to8', 'h8plus']);
export const photoPoseSchema = z.enum(['front', 'side', 'back']);
export type PhotoPoseCode = z.infer<typeof photoPoseSchema>;
export const POSE_LABEL: Record<PhotoPoseCode, string> = { front: 'Frente', side: 'Lado', back: 'Costas' };
export const POSES: PhotoPoseCode[] = ['front', 'side', 'back'];

// ---- entradas ---------------------------------------------------------------------------------------------------

export const weighInInput = z.object({
  clientId: z.string().uuid().optional(),
  date: isoDate.optional(),
  weightKg: z.number().min(30).max(250),
});
export type WeighInInput = z.infer<typeof weighInInput>;

export const checkinInput = z.object({
  date: isoDate.optional(),
  weightKg: z.number().min(30).max(250),
  waistCm: z.number().min(40).max(200),
});
export type CheckinInput = z.infer<typeof checkinInput>;

export const sleepInput = z.object({ bucket: sleepBucketSchema, energy: z.number().int().min(1).max(5) });
export type SleepInput = z.infer<typeof sleepInput>;

export const noteInput = z.object({ text: z.string().max(4000) });
export type NoteInput = z.infer<typeof noteInput>;

export const historyMonthQuery = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/, 'Mês no formato AAAA-MM') });
export const dateParam = z.object({ date: isoDate });

/** PATCH /plan: metas do plano. `kcalFloor` nunca muda (5.1) e é recusado. */
export const planPatch = z
  .object({
    name: z.string().min(1).max(80),
    startDate: isoDate,
    startWeightKg: z.number().min(30).max(250),
    goalWeightKg: z.number().min(30).max(250),
    kcalTarget: z.number().int().min(1200).max(5000),
    proteinG: z.number().int().min(0).max(400),
    carbsG: z.number().int().min(0).max(800),
    fatG: z.number().int().min(0).max(300),
    waterMl: z.number().int().min(500).max(8000),
    waterMlGameDay: z.number().int().min(500).max(8000),
    sleepMinH: z.number().min(3).max(12),
    sleepMaxH: z.number().min(3).max(14),
    excludedFoods: z.array(z.string().min(1).max(60)).max(40),
  })
  .partial()
  .strict();
export type PlanPatch = z.infer<typeof planPatch>;

export const adjustmentInput = z.object({
  kind: z.enum(['kcal_minus', 'kcal_plus', 'early_deload']),
  alertId: z.string().uuid().optional(),
  reason: z.string().max(200).optional(),
});
export type AdjustmentInput = z.infer<typeof adjustmentInput>;

export const settingsPatch = z
  .object({
    timezone: z.string().min(3).max(64),
    wakeTime: hhmm,
    bedTime: hhmm,
    trainingTime: hhmm,
    gameWeekday: z.number().int().min(1).max(7),
    gameTime: hhmm,
    studySuggestedTime: hhmm,
    gymName: z.string().max(80).nullable(),
  })
  .partial()
  .strict();
export type SettingsPatch = z.infer<typeof settingsPatch>;

// ---- respostas --------------------------------------------------------------------------------------------------

export type WeighInDTO = { id: string; date: string; weightKg: number; measuredAt: string };

export type PhotoDTO = { id: string; pose: PhotoPoseCode; thumbUrl: string; fullUrl: string; width: number | null; height: number | null };

export type CheckinDTO = {
  id: string;
  date: string;
  dayNumber: number;
  weightKg: number;
  waistCm: number;
  photos: PhotoDTO[];
};

export type CheckinsResponse = {
  checkins: CheckinDTO[];
  day1: CheckinDTO | null;
  /** Check-in a fazer (o dia 1 se ainda não existe; senão o próximo dia 30, 60…). */
  next: { dayNumber: number; date: string; month: number; targetKg: number | null; targetDate: string | null };
  /** O seguinte ao `next` ("Próximo check-in: segunda, 14/dez (dia 60)"). */
  after: { dayNumber: number; date: string };
  /** Check-in já salvo para o `next` (edição). */
  current: CheckinDTO | null;
  today: string;
};

export type CheckinSaveResult = {
  checkin: CheckinDTO;
  deltaKg: number | null;
  deltaWaistCm: number | null;
  avg7: number | null;
  monthClosed: ProgressAlertDraft | null;
};

export type MachineLoad = { exerciseId: string; name: string; firstKg: number; currentKg: number };

export type ActiveAdjustment = { id: string; kind: 'kcal_minus' | 'kcal_plus' | 'early_deload'; kcalDelta: number; planWeek: number; appliedAt: string };

export type ProgressResponse = {
  today: string;
  startDate: string;
  dayNumber: number;
  planWeek: number;
  totalDays: number;
  weight: {
    avg7: number | null;
    change: number | null;
    startKg: number;
    goalKg: number;
    chart: WeightChart;
    /** Semanas 1 e 2: nota da creatina. */
    waterWeeks: boolean;
    nextWeighDay: string;
    weighedToday: boolean;
    lastKg: number | null;
  };
  waist: { cm: number; date: string; nextDate: string } | null;
  runTest: { durationS: number; date: string; planWeek: number; nextWeek: number | null } | null;
  rhythm: {
    status: 'ok' | 'stalled' | 'too_fast' | 'floor';
    month: number;
    monthTargetKg: number | null;
    monthTargetDate: string | null;
    suggestion: { kind: 'kcal_minus' | 'kcal_plus'; title: string; body: string } | null;
    floorMessage: string | null;
    active: ActiveAdjustment[];
    kcalTarget: number;
  };
  months: { n: number; kg: number; date: string; current: boolean; final: boolean }[];
  /** Avisos da faixa screen:progresso calculados na leitura (água das semanas 1–2, mês fechado). */
  alerts: ProgressAlertDraft[];
  photos: { checkinId: string; date: string; dayNumber: number; items: PhotoDTO[] } | null;
  nextCheckin: { dayNumber: number; date: string };
  machines: MachineLoad[];
  constancy: Constancy & { gameDays: number };
  goals: { kcal: number; proteinG: number; waterMl: number; studyMin: number; languages: number };
  forecast: Forecast;
};

export type HistoryMonthDay = { date: string; state: DayState; isToday: boolean; selectable: boolean };
export type HistoryMonthResponse = { month: string; today: string; startDate: string; days: HistoryMonthDay[] };

export type HistoryDayResponse = {
  date: string;
  isToday: boolean;
  dayNumber: number;
  title: string;
  sub: string;
  state: DayState;
  summary: { label: string; value: string; meta: string }[];
  meals: { slot: string; time: string; name: string; desc: string; kcal: number; proteinG: number; isFreeMeal: boolean }[];
  workout: {
    title: string;
    meta: string;
    status: string;
    exercises: { name: string; series: string; load: string; gym: boolean; up: string | null }[];
  } | null;
  study: { kanji: string; name: string; detail: string; minutes: number }[];
  runs: { title: string; detail: string }[];
  note: string;
  isGameDay: boolean;
  gameFeedback: boolean | null;
  totals: { kcal: number; proteinG: number; waterMl: number; studyMinutes: number };
};

export type PlanDTO = {
  id: string;
  name: string;
  startDate: string;
  startWeightKg: number;
  goalWeightKg: number;
  kcalTarget: number;
  kcalFloor: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  waterMl: number;
  waterMlGameDay: number;
  sleepMinH: number;
  sleepMaxH: number;
  adaptationWeeks: number;
  deloadEvery: number;
  breadDelta: number;
  excludedFoods: string[];
  monthlyTargets: { month: number; targetKg: number; targetDate: string }[];
  adjustments: { id: string; kind: string; kcalDelta: number; planWeek: number; reason: string; appliedAt: string; revertedAt: string | null }[];
  rules: string[];
  mealSlots: { slot: string; time: string; label: string }[];
  settings: SettingsDTO;
};

export type ChecklistItemDTO = { code: string; label: string; doneLabel: string; sort: number; doneAt: string | null };
export type ChecklistResponse = { items: ChecklistItemDTO[]; done: number; total: number };

export type SettingsDTO = {
  timezone: string;
  wakeTime: string;
  bedTime: string;
  trainingTime: string;
  gameWeekday: number;
  gameTime: string;
  studySuggestedTime: string;
  gymName: string | null;
};

export type ProfileResponse = {
  user: { name: string; initial: string; age: number | null; heightCm: number | null; email: string };
  plan: PlanDTO;
  current: { avg7: number | null; remainingKg: number | null; progress: number };
  forecast: Forecast;
  languages: { id: string; code: string; kanji: string; name: string; dailyGoalMin: number; method: string; active: boolean }[];
  studyReminder: { daily: string; risk: string };
  checklist: ChecklistResponse;
};

/** Rótulos do checklist do começo (11.10): pendente e feito, como no quadro Perfil. */
export const CHECKLIST_LABELS: Record<string, { pending: string; done: string }> = {
  livup_order: { pending: 'Fazer o pedido da Liv Up', done: 'Pedido da Liv Up feito' },
  market: { pending: 'Mercado + whey de 900 g + creatina', done: 'Mercado + whey de 900 g + creatina' },
  eggs: { pending: 'Cozinhar os primeiros 10 ovos', done: 'Primeiros 10 ovos cozidos' },
  day1: { pending: 'Peso, cintura e fotos do dia 1', done: 'Peso, cintura e fotos do dia 1' },
  run_test: { pending: 'Teste de 2 km na esteira', done: 'Teste de 2 km na esteira' },
  calendar: { pending: 'Treinos no calendário com horário fixo', done: 'Treinos no calendário com horário fixo' },
  blood_tests: { pending: 'Exames de sangue', done: 'Exames de sangue feitos' },
};
